<template>
  <ul :class="`nav navbar-nav ${trialWidth ? 'trial-width' : ''}`">
    <A11y-Dropdown
      :dropdown-props="{
        tag:'li',
        class:'gb-navbar__user-menu',
        ref:'userMenuToggle'
      }"
      :button-props="{
        class:'gb-navbar__user-menu__toggle constrain-width ellipsis',
        'data-role':'trigger',
        'aria-label':'User menu'
      }"
      :dom-attributes="{'role':'menu', id:'gb-navbar-user-menu-dropdown'}"
    >
      <template slot="button-contents">
        <div class="user-menu-img" v-if="profileImageUrl">
          <img :src="profileImageUrl" alt="">
        </div>
        {{ user.name }} <b class="caret"/>
        <br>
        <small>{{ user.title }}</small>
      </template>
      <template slot="dropdown-items">
        <template v-if="showAnyAdminOptions">
          <li v-if="isSuperAdmin" role="menuitem"><AdminToggle flag-type="editor" /></li>
          <li v-if="isSuperAdmin || isTestUser || isGoalbookBusinessUser" role="menuitem"><AdminToggle flag-type="beta" /></li>
          <li v-if="isSuperAdmin" role="menuitem">
            <a
              role="button"
              @click="setWikiTextPreviewer(true)"
              tabindex="0">
              <small>Wikitext Markdown Previewer</small>
            </a>
          </li>
          <li role="menuitem" v-if="showContentProfileSelector">
            <a
              role="button"
              @click="setIsProfileSelectorOpen(true)"
              tabindex="0">{{ this.isSuperAdmin || this.isTestUser || this.isGoalbookBusinessUser ? 'Content Profile Selector' : 'Select Your State' }}</a>
          </li>
          <li v-if="isSuperAdmin || isTestUser || isGoalbookBusinessUser" role="menuitem">
            <a
              role="button"
              @click="showVisibilityModal = true"
              tabindex="0">Visibility Settings</a>
          </li>
          <li v-if="isTestUser" role="menuitem">
            <a
              role="button"
              @click="setLicenseSelectorOpen(true)"
              tabindex="0">License Selector</a>
          </li>
          <li class="divider" role="separator"/>
        </template>

        <!-- Non-admin menu items -->
        <li role="menuitem"><router-link :to="{name: 'Favorites'}">Your Favorites</router-link></li>
        <li v-if="!isSuperAdmin" role="menuitem"><a
          tabindex="0"
          @click="trackLinkClick('Clicked Account Settings', {ref: 'navbar'})"
          :href="account">Account Settings</a></li>
        <li role="menuitem"><a
          rel="nofollow"
          tabindex="0"
          @click="trackLinkClick('Clicked Sign Out', {ref: 'navbar'})"
          :href="signOut">Sign out</a></li>
        <template v-if="isSchoolAdmin">
          <li class="divider" role="separator"/>
          <li role="menuitem"><a
            tabindex="0"
            @click="trackLinkClick('Clicked Toolkit User List', {ref: 'navbar'})"
            :href="userList">Toolkit User List</a></li>
        </template>
        <li role="menuitem" v-if="isSuperAdmin || isGoalbookBusinessUser|| (isSchoolAdmin && !iodVisibility)"><a
          tabindex="0"
          @click="trackLinkClick('Clicked Analytics Dashboard', {ref: 'navbar'})"
          :href="analyticsDashboard">Analytics Dashboard</a></li>
        <template v-if="isGoalbookBusinessUser">
          <li class="divider" role="separator"/>
          <li role="menuitem"><AdminToggle flag-type="beta" /></li>
        </template>
        <template v-if="pathwaysAccess || isSuperAdmin">
          <li class="divider" role="separator"/>
          <li role="menuitem"><a
            rel="nofollow"
            tabindex="0"
            @click="trackLinkClick('Clicked Goalbook Pathways', {ref: 'navbar'})"
            :href="pathwaysHome">Switch to <span class="bold">Goalbook Pathways</span></a></li>
        </template>
      </template>
    </A11y-Dropdown>
    <ContentProfileSelectorContainer />
    <LicenseSelectorContainer />
    <VisibilitySettingsModal :show="showVisibilityModal" @close="showVisibilityModal = false" />
  </ul>
</template>

  <script>
  import { mapState, mapGetters, mapMutations } from 'vuex'
  import { goalbookUrls, toolkitUrls, pathwaysUrls } from '@/links'
  import AdminToggle from './AdminToggle'
  import ContentProfileSelectorContainer from '@/contentprofile/components/ContentProfileSelectorContainer'
  import LicenseSelectorContainer from './LicenseSelectorContainer'
  import VisibilitySettingsModal from './VisibilitySettingsModal'
  import A11yDropdown from '@/components/A11yDropdown'
  import homeMixin from '@/components/home/mixins'

  export default {
    name: 'UserMenu',
    mixins: [homeMixin],
    components: {
      A11yDropdown,
      AdminToggle,
      ContentProfileSelectorContainer,
      LicenseSelectorContainer,
      VisibilitySettingsModal,
    },
    data() {
      return {
        open: false,
        showVisibilityModal: false,
        signOut: goalbookUrls.signOut,
        account: toolkitUrls.account,
        userList: toolkitUrls.userList,
        analyticsDashboard: `${toolkitUrls.home}/v/admin/analytics`,
        pathwaysHome: goalbookUrls.pathways,
      }
    },
    computed: {
      ...mapState('user', ['user']),
      ...mapGetters('visibility', ['canChangeProfile', 'iodVisibility']),
      ...mapGetters('user', [
        'isSuperAdmin',
        'isSchoolAdmin',
        'isTestUser',
        'isGoalbookBusinessUser',
        'isLicenseExpired',
        'isEnterpriseEndingSoon',
        'isPremium',
        'isEnterprise',
        'userDomainUuid'
      ]),
      ...mapState('user', {
        pathwaysAccess: state => state.pathways_access,
      }),
      ...mapGetters('contentProfile', ['profileImageUrl']),
      trialWidth() {
        return (
          !this.isSuperAdmin &&
          (!this.isEnterprise ||
            !this.isPremium ||
            this.isEnterpriseEndingSoon ||
            this.isLicenseExpired)
          )
      },
      showContentProfileSelector() {
        return (this.canChangeProfile && this.userDomainUuid) || this.isSuperAdmin || this.isTestUser || this.isGoalbookBusinessUser
      },
      showAnyAdminOptions() {
        return this.isSuperAdmin || this.isTestUser || this.isGoalbookBusinessUser || this.showContentProfileSelector
      }
    },
    methods: {
      ...mapMutations(['setWikiTextPreviewer']),
      ...mapMutations('contentProfile', ['setIsProfileSelectorOpen']),
      ...mapMutations('user', ['setLicenseSelectorOpen']),
    }
  }
  </script>

  <style lang="less">
  @import '~@/variables.less';

  .gb-navbar__user-menu {
    .gb-navbar__user-menu__toggle {
      width: 260px;
      padding: 7px;
      cursor: pointer;
      small {
        display: block;
        margin-top: -4px;
        color: white;
        font-size: 11px;
      }
      b.caret {
        border-width: 4px;
      }
    }
    .user-menu-img {
      float: left;
      margin-right: 10px;
      img {
        height: 30px;
        width: 30px;
      }
    }

    @media screen and (min-width: @screen-md) and (max-width: @screen-md-max) { // 992 to 1199
      .constrain-width {
        max-width: 260px;
      }
      .caret {
          display: inline-block !important;
          position: unset !important;
          right: 2px;
          top: 50%;
          margin-top: -3px;
        }
    }
    @media screen and (min-width: @screen-sm) and (max-width: @screen-sm-max) { //768 to 991
      .constrain-width {
        max-width: 110px;
        .caret {
          display: block;
          position: absolute;
          right: 2px;
          top: 50%;
          margin-top: -3px;
        }
      }
    }
    & > .dropdown-menu {
      width: 100%;
      min-width: initial;
      & > li > a {
        white-space: normal;
        cursor: pointer;
    }

      border-top: 0;
      box-shadow: none;
      & > li > a {
        background: white !important;
        padding-left: 10px;
        padding-right: 10px;
        &:hover {
          text-decoration: underline;
        }
        color: #333333 !important;
      }
    }

    .bold {
        font-weight: bold;
    }
  }
  .trial-width {
      width: 200px;
  }
  </style>
